import * as React from "react";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { assign, createMachine } from "xstate";
import { css, keyframes } from "@nju/css";
import { MemberOrchestratorContext } from "@nju/data-access-portal-member";
import { IOfferMode } from "@nju/data-access-portal-offers";
import {
  fetchOrchestrators,
  getOrchestratorForMode,
  IFtthOrchestrator,
  IMobileOrchestrator,
  IMultiOrderOrchestrator,
  IOrchestrators,
  OrchestratorsContext,
} from "@nju/data-access-portal-orchestrator";
import { isError } from "@nju/result";
import { salesRoutes } from "@nju/routes";
import { CircleLoader, GeneralErrorNotification } from "@nju/ui";
import { redirectToOrchestratorState } from "./redirectToOrchestratorState";
import { resolvePathFromOrchestratorState } from "./resolvePathFromOrchestratorState";
import { RespectMode } from "./RespectMode";

type IStateOrStates =
  | IFtthOrchestrator["state"]
  | IMobileOrchestrator["state"]
  | IMultiOrderOrchestrator["state"]
  | "?"
  | Array<IFtthOrchestrator["state"] | "?">
  | Array<IMobileOrchestrator["state"] | "?">
  | Array<IMultiOrderOrchestrator["state"] | "?">;

interface IRespectOrchestratorStateProps {
  state: IStateOrStates;
  forceIgnoreProperStateParamName?: string;
  refreshInterval?: number;
  mode: IOfferMode;
  allowMissingState?: boolean;
}

interface IContext {
  orchestrators: IOrchestrators | null;
  refreshInterval?: number;
  allowMissingState?: boolean;
  mode: IOfferMode;
  stateOrStates: IStateOrStates;
  shouldIgnoreProperState?: boolean;
  isPristine: boolean;
}

type IEvent =
  | { type: "RELOAD" }
  | { type: "GENERAL_ERROR" }
  | { type: "OK"; orchestrators: IOrchestrators }
  | { type: "MISSING_ORCHESTRATOR" }
  | {
      type: "INVALID_STATE";
      state:
        | IFtthOrchestrator["state"]
        | IMobileOrchestrator["state"]
        | IMultiOrderOrchestrator["state"];
    };

const machine = createMachine<IContext, IEvent>(
  {
    context: {
      mode: "ftth",
      stateOrStates: "?",
      isPristine: true,
      // eslint-disable-next-line unicorn/no-null
      orchestrators: null,
    },
    initial: "load",
    states: {
      idle: {
        on: {
          RELOAD: {
            target: "load",
          },
        },
        after: [
          {
            delay: (context) => {
              return context.refreshInterval || Number.POSITIVE_INFINITY;
            },
            cond: (context) => {
              return !!context.refreshInterval;
            },
            target: "load",
          },
        ],
      },
      load: {
        invoke: {
          src: ({
            shouldIgnoreProperState,
            allowMissingState,
            mode,
            stateOrStates,
          }) => {
            return async (callback) => {
              const result = await fetchOrchestrators();
              if (isError(result)) {
                return callback({ type: "GENERAL_ERROR" });
              }

              const orchestrator = getOrchestratorForMode(result.value, mode);

              if (shouldIgnoreProperState) {
                return callback({ type: "OK", orchestrators: result.value });
              }

              if (allowMissingState && !orchestrator) {
                return callback({ type: "OK", orchestrators: result.value });
              }

              if (!orchestrator) {
                return callback({ type: "MISSING_ORCHESTRATOR" });
              }

              if (checkIfStateIsCorrect(stateOrStates, orchestrator.state)) {
                return callback({ type: "OK", orchestrators: result.value });
              }

              console.warn(
                `[nju][RespectOrchestratorState:invalid-state]: ${stateOrStates} vs ${
                  orchestrator.state
                } => ${resolvePathFromOrchestratorState(orchestrator.state)}`,
              );

              return callback({
                type: "INVALID_STATE",
                state: orchestrator.state,
              });
            };
          },
        },
        on: {
          GENERAL_ERROR: {
            target: "error",
          },
          INVALID_STATE: {
            actions: "onRedirectToOrchestratorState",
            target: "invalidState",
          },
          OK: {
            actions: "orchestratorResolved",
            target: "idle",
          },
          MISSING_ORCHESTRATOR: {
            actions: "onMissingOrchestrator",
          },
        },
      },
      error: {},
      invalidState: {},
    },
  },
  {
    actions: {
      orchestratorResolved: assign((context, event) => {
        if (event.type === "OK") {
          return {
            orchestrators: event.orchestrators,
            isPristine: false,
          };
        }

        return context;
      }),
    },
  },
);

function checkIfStateIsCorrect(allowed: string | string[], provided: string) {
  return typeof allowed === "string"
    ? allowed === provided
    : allowed.includes(provided);
}

function RespectOrchestratorStateInternal({
  state: stateOrStates,
  mode,
  forceIgnoreProperStateParamName = "zmien",
  children,
  refreshInterval,
  allowMissingState = false,
}: React.PropsWithChildren<IRespectOrchestratorStateProps>) {
  const router = useRouter();
  const [{ context, matches }] = useMachine(machine, {
    actions: {
      onMissingOrchestrator: () => {
        router.push(salesRoutes.panel.get());
        return;
      },
      onRedirectToOrchestratorState: (_, event) => {
        if (event.type === "INVALID_STATE") {
          redirectToOrchestratorState({ state: event.state, mode });
        }
      },
    },
    context: {
      refreshInterval,
      allowMissingState,
      mode,
      stateOrStates,
      shouldIgnoreProperState: forceIgnoreProperStateParamName in router.query,
    },
  });

  const { orchestrators, isPristine } = context;

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return router.push(salesRoutes.panel.get());
        }}
      />
    );
  }

  if (isPristine || matches("invalidState")) {
    return (
      <div className={loaderWrapper()}>
        <CircleLoader />
      </div>
    );
  }

  return (
    <OrchestratorsContext.Provider value={orchestrators}>
      <MemberOrchestratorContext.Provider
        value={{
          mobile: undefined,
          changeMsisdn: undefined,
          mobileReactivation: undefined,
          tv: undefined,
        }}
      >
        {children}
      </MemberOrchestratorContext.Provider>
    </OrchestratorsContext.Provider>
  );
}

function RedirectToOrchestratorState(
  props: React.PropsWithChildren<{ mode: IOfferMode }>,
) {
  return <>{props.children}</>;
}

export function RespectOrchestratorState(
  props: React.PropsWithChildren<
    Omit<IRespectOrchestratorStateProps, "mode"> & {
      mode: IOfferMode | IOfferMode[];
    }
  >,
) {
  return (
    <RespectMode mode={props.mode}>
      {({ mode }) => {
        return (
          <RedirectToOrchestratorState mode={mode}>
            <RespectOrchestratorStateInternal {...props} mode={mode} />
          </RedirectToOrchestratorState>
        );
      }}
    </RespectMode>
  );
}

const show = keyframes({
  "0%": { opacity: 0 },
  "1%": { opacity: 0 },
  "100%": { opacity: 1 },
});

const loaderWrapper = css({
  opacity: 0,
  display: "flex",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  animationName: `${show}`,
  animationDelay: "0.3s",
  animationDuration: "1s",
  animationFillMode: "forwards",
});
